import {
  X,
  PlayCircle,
  BookOpen,
  Sparkles,
  GraduationCap,
  MessageSquare,
  Headphones,
  HelpCircle,
  ArrowRight,
  FileText,
  Lightbulb,
  Info,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { HelpTopic } from '../../hooks/useHelp';
import './help.css';

// Base URL para vídeos no Firebase Storage
const HELP_VIDEO_BASE = import.meta.env.VITE_HELP_VIDEO_BASE || '/help/videos';
const HELP_SUBTITLE_BASE = import.meta.env.VITE_HELP_SUBTITLE_BASE || '/help/subtitles';

interface HelpDialogProps {
  title: string;
  topics: HelpTopic[];
  selectedTopicId: string | null;
  onSelectTopic: (id: string) => void;
  onClose: () => void;
  hasWalkthrough?: boolean;
  onStartWalkthrough?: () => void;
  /** Tours automáticos ligados/desligados */
  toursEnabled?: boolean;
  onToggleTours?: (enabled: boolean) => void;
  /** Regenera o roteiro com IA (ignora cache) */
  onRegenerateTour?: () => void;
  regeneratingTour?: boolean;
}

/** Formata tags de feature técnicas para rótulos legíveis e agradáveis */
function formatFeatureTag(feature?: string): string {
  if (!feature) return 'Guia da Tela';
  const f = feature.toLowerCase().trim();
  if (f.includes('kpi')) return 'Métricas & Indicadores';
  if (f.includes('filtro')) return 'Filtros de Busca';
  if (f.includes('busca')) return 'Busca Rápida';
  if (f.includes('export')) return 'Exportação de Dados';
  if (f.includes('upload')) return 'Envio de Arquivos';
  if (f.includes('datatable') || f.includes('tabela')) return 'Tabela de Dados';
  if (f.includes('expand')) return 'Seções Expansíveis';
  if (f.includes('badge')) return 'Status & Identificadores';
  if (f.includes('calendario')) return 'Calendário & Prazos';
  if (f.includes('grafico') || f.includes('chart')) return 'Gráficos & Tendências';
  if (f.includes('aula') || f.includes('catálogo') || f.includes('catalogo')) return 'Aula Gravada';
  return feature.replace(/_/g, ' ');
}

/**
 * Diálogo de Ajuda Contextual do Aluno Conectado:
 * - Exibe estritamente orientações pertinentes à página ativa
 * - Caso não haja orientações cadastradas para a tela, exibe estado acolhedor com botão de suporte pelo chat
 * - Se houver tópicos cadastrados, apresenta layout moderno com vídeo funcional ou card textual alternativo (nunca player preto quebrado)
 * - Integração nativa com o canal de suporte administrativo via chat
 */
export function HelpDialog({
  title,
  topics,
  selectedTopicId,
  onSelectTopic,
  onClose,
  hasWalkthrough = false,
  onStartWalkthrough,
  toursEnabled = true,
  onToggleTours,
  onRegenerateTour,
  regeneratingTour = false,
}: HelpDialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);

  const selectedTopic = topics.find((t) => t.id === selectedTopicId) || topics[0];
  const location = useLocation();
  const navigate = useNavigate();

  const openEad = () => {
    const segments = location.pathname.split('/');
    const valid = ['admin', 'master', 'municipal', 'estadual', 'professor', 'equipe', 'responsavel', 'aluno'];
    const segment = segments.find((s) => valid.includes(s)) || 'master';
    onClose();
    navigate(`/${segment}/ead`);
  };

  // Fechar com tecla ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Fechar ao clicar no overlay de fundo
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  // Resetar estado de erro e carregar novo vídeo quando troca tópico
  useEffect(() => {
    setVideoError(false);
    if (videoRef.current && selectedTopic?.video) {
      videoRef.current.load();
    }
  }, [selectedTopic?.id, selectedTopic?.video]);

  const resolveUrl = (pathVal: string | undefined, base: string) => {
    if (!pathVal) return null;
    if (pathVal.startsWith('http://') || pathVal.startsWith('https://')) {
      return pathVal;
    }
    if (pathVal.startsWith('ecad/') || pathVal.startsWith('/ecad/')) {
      const cleanPath = pathVal.startsWith('/') ? pathVal.substring(1) : pathVal;
      const apiUrl = import.meta.env.VITE_API_URL;
      if (apiUrl && (apiUrl.startsWith('http://') || apiUrl.startsWith('https://'))) {
        try {
          const url = new URL(apiUrl);
          return `${url.origin}/${cleanPath}`;
        } catch {
          // ignore
        }
      }
      return `/${cleanPath}`;
    }
    return `${base}/${pathVal}`;
  };

  const videoUrl = resolveUrl(selectedTopic?.video, HELP_VIDEO_BASE);
  const subtitleUrl = resolveUrl(selectedTopic?.legenda, HELP_SUBTITLE_BASE);

  // Abertura canônica do chat interno de suporte administrativo
  const handleOpenSupportChat = () => {
    onClose();
    window.dispatchEvent(
      new CustomEvent('ac:chat:open', {
        detail: {
          target: 'suporte',
          subject: `Ajuda na tela: ${title}`,
          context: location.pathname,
          initialMessage: `Olá, equipe de suporte! Preciso de orientações sobre a página "${title}" (${location.pathname}).`,
        },
      })
    );
  };

  return (
    <div
      ref={overlayRef}
      className="ecad-help-dialog-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={`Ajuda — ${title}`}
    >
      <div className="ecad-help-dialog">
        {/* Header do Diálogo */}
        <div className="ecad-help-dialog-header">
          <h2>
            <BookOpen size={18} className="ecad-help-header-icon" />
            <span>Ajuda e Orientações — {title}</span>
          </h2>
          <button
            type="button"
            className="ecad-help-dialog-close"
            onClick={onClose}
            aria-label="Fechar modal de ajuda"
          >
            <X size={18} />
          </button>
        </div>

        {/* Corpo do Diálogo */}
        {topics.length === 0 ? (
          /* ESTADO VAZIO: Nenhuma orientação cadastrada para esta tela */
          <div className="ecad-help-empty-container">
            <div className="ecad-help-empty-card">
              <div className="ecad-help-empty-icon-wrap">
                <Headphones size={36} className="ecad-help-empty-icon" />
                <span className="ecad-help-pulse-dot" title="Suporte online e disponível" />
              </div>

              <div className="ecad-help-empty-badge">
                <Info size={13} />
                <span>Atendimento & Orientações</span>
              </div>

              <h3 className="ecad-help-empty-title">
                Orientações desta Página
              </h3>

              <p className="ecad-help-empty-desc">
                Ainda não há tutoriais, vídeos ou orientações cadastradas especificamente para esta tela no momento.
              </p>

              <div className="ecad-help-empty-support-box">
                <p className="ecad-help-empty-support-text">
                  Ficou com alguma dúvida ou precisa de auxílio para navegar nas ferramentas desta página? Nossa equipe de suporte administrativo está disponível pelo chat em tempo real para orientar você!
                </p>

                <button
                  type="button"
                  className="ecad-help-chat-primary-btn"
                  onClick={handleOpenSupportChat}
                >
                  <MessageSquare size={17} />
                  <span>Falar com o Suporte pelo Chat</span>
                  <ArrowRight size={15} style={{ opacity: 0.8 }} />
                </button>
              </div>

              {hasWalkthrough && onStartWalkthrough && (
                <div className="ecad-help-empty-walkthrough">
                  <button
                    type="button"
                    className="ecad-help-walkthrough-btn"
                    onClick={() => {
                      onClose();
                      onStartWalkthrough();
                    }}
                  >
                    <Sparkles size={15} />
                    <span>Iniciar tour interativo pelos menus</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ESTADO COM TÓPICOS: Painel de Categorias + Conteúdo */
          <div className="ecad-help-dialog-body">
            {/* Painel Esquerdo: Lista de Tópicos */}
            <div className="ecad-help-categories">
              <div className="ecad-help-cat-header">Tópicos da Tela</div>
              {topics.map((topic, index) => (
                <button
                  key={topic.id}
                  type="button"
                  className={`ecad-help-cat-item ${topic.id === selectedTopic?.id ? 'active' : ''}`}
                  onClick={() => onSelectTopic(topic.id)}
                >
                  <span className="ecad-help-cat-num">{index + 1}</span>
                  <span className="ecad-help-cat-label">
                    {topic.titulo}
                  </span>
                </button>
              ))}
            </div>

            {/* Painel Direito: Conteúdo do Tópico Selecionado */}
            <div className="ecad-help-content">
              {/* Banner de Treinamento Interativo (se habilitado) */}
              {hasWalkthrough && (
                <div className="ecad-help-tour-banner">
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <Sparkles size={18} style={{ color: 'hsl(var(--primary))', flexShrink: 0 }} />
                      <div>
                        <h4 style={{ margin: 0, fontSize: '0.84rem', fontWeight: 700, color: 'hsl(var(--foreground))' }}>
                          Treinamento Interativo Guiado
                        </h4>
                        <p style={{ margin: '2px 0 0', fontSize: '0.74rem', color: 'hsl(var(--muted-foreground))' }}>
                          Destaque passo a passo de cada elemento da tela para facilitar seu aprendizado.
                        </p>
                      </div>
                    </div>
                    {onStartWalkthrough && (
                      <button
                        type="button"
                        onClick={onStartWalkthrough}
                        className="ecad-help-start-tour-btn"
                      >
                        <PlayCircle size={14} /> {toursEnabled ? 'Iniciar tour' : 'Ligar e iniciar'}
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                    {onToggleTours && (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.76rem', fontWeight: 600, color: 'hsl(var(--foreground))', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={toursEnabled}
                          onChange={(e) => onToggleTours(e.target.checked)}
                        />
                        Tours automáticos na 1ª visita
                      </label>
                    )}
                    {onRegenerateTour && (
                      <button
                        type="button"
                        disabled={regeneratingTour}
                        onClick={onRegenerateTour}
                        className="ecad-help-regen-tour-btn"
                      >
                        <Sparkles size={13} />
                        {regeneratingTour ? 'Gerando…' : 'Regenerar tour (IA)'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={openEad}
                      className="ecad-help-open-ead-btn"
                    >
                      <GraduationCap size={14} /> Centro de Ensino (EAD)
                    </button>
                  </div>
                </div>
              )}

              {selectedTopic && (
                <>
                  <div className="ecad-help-topic-header">
                    <h3 className="ecad-help-content-title">{selectedTopic.titulo}</h3>
                    <span className="ecad-help-feature-tag">
                      {formatFeatureTag(selectedTopic.feature)}
                    </span>
                  </div>

                  {/* Player de Vídeo ou Card Ilustrado de Orientação */}
                  {videoUrl && !videoError ? (
                    <div className="ecad-help-video-container">
                      <video
                        ref={videoRef}
                        className="ecad-help-video"
                        controls
                        preload="metadata"
                        crossOrigin="anonymous"
                        onError={() => setVideoError(true)}
                      >
                        <source src={videoUrl} type="video/webm" />
                        <source src={videoUrl.replace('.webm', '.mp4')} type="video/mp4" />
                        {subtitleUrl && (
                          <track
                            kind="subtitles"
                            src={subtitleUrl}
                            srcLang="pt-BR"
                            label="Português"
                            default
                          />
                        )}
                        Seu navegador não suporta a reprodução deste vídeo.
                      </video>
                    </div>
                  ) : (
                    <div className="ecad-help-reading-card">
                      <div className="ecad-help-reading-header">
                        <Lightbulb size={20} className="ecad-help-reading-icon" />
                        <div>
                          <strong>Guia Prático de Utilização</strong>
                          <span>Orientações em texto para esta funcionalidade</span>
                        </div>
                      </div>
                      <p className="ecad-help-reading-text">{selectedTopic.descricao}</p>
                    </div>
                  )}

                  {/* Descrição adicional quando o vídeo estiver presente */}
                  {videoUrl && !videoError && (
                    <p className="ecad-help-content-desc">{selectedTopic.descricao}</p>
                  )}

                  {/* Barra fixa de acionamento do suporte */}
                  <div className="ecad-help-support-bar">
                    <div className="ecad-help-support-bar-text">
                      <Headphones size={18} className="ecad-help-support-bar-icon" />
                      <div>
                        <strong>Ainda ficou com alguma dúvida sobre esta página?</strong>
                        <span>Nossa equipe administrativa e pedagógica pode orientar você diretamente pelo chat.</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="ecad-help-support-btn"
                      onClick={handleOpenSupportChat}
                    >
                      <MessageSquare size={14} />
                      <span>Chamar Suporte pelo Chat</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
